import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Radar,
    CheckCheck,
    Trash2,
    Check,
    RefreshCw,
    ExternalLink,
    BellRing,
    Calendar,
    Lightbulb,
    TrendingUp,
    AlertTriangle,
} from 'lucide-react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassTabs } from '../ui/GlassTabs';
import { GlassBadge } from '../ui/GlassBadge';
import { ModuleHeader } from '../ui/ModuleHeader';
import {
    getNotifications,
    getWeeklyCheckin,
    markNotificationRead,
    markAllNotificationsRead,
    dismissNotification,
    deleteNotification,
    runCoordinatorChecks,
    Notification,
} from '../../services/api';
import './Coordinator.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WeeklyCheckin {
    summary: string;
    highlights: string[];
    recommended_actions: { action: string; description: string; url: string }[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString();
}

function typeLabel(type: string): string {
    const map: Record<string, string> = {
        cadence_alert: 'Publishing',
        campaign_deadline: 'Campaign',
        weekly_checkin: 'Weekly',
        content_suggestion: 'Suggestion',
        performance_drop: 'Performance',
        uncontacted_leads: 'Leads',
    };
    return map[type] || type;
}

function typeBadgeVariant(type: string): 'success' | 'warning' | 'danger' | 'info' | 'default' {
    const map: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'default'> = {
        cadence_alert: 'warning',
        campaign_deadline: 'danger',
        weekly_checkin: 'info',
        content_suggestion: 'success',
    };
    return map[type] || 'default';
}

// ---------------------------------------------------------------------------
// Settings (stored in localStorage for simplicity)
// ---------------------------------------------------------------------------

const SETTINGS_KEY = 'coordinator_settings';

const DEFAULT_SETTINGS: Record<string, boolean> = {
    cadence_alert: true,
    campaign_deadline: true,
    weekly_checkin: true,
    content_suggestion: true,
};

const SETTINGS_META = [
    {
        key: 'cadence_alert',
        name: 'Publishing Cadence Alerts',
        desc: 'Notify me when I have not published content for more than 7 days.',
        icon: Calendar,
    },
    {
        key: 'campaign_deadline',
        name: 'Campaign Deadline Warnings',
        desc: 'Alert me when an active campaign is ending within 3 days.',
        icon: AlertTriangle,
    },
    {
        key: 'weekly_checkin',
        name: 'Weekly Check-In',
        desc: 'Receive a weekly summary with highlights and recommended actions every Monday.',
        icon: BellRing,
    },
    {
        key: 'content_suggestion',
        name: 'Content Opportunity Suggestions',
        desc: 'Get periodic reminders to review AI-generated content gap suggestions.',
        icon: Lightbulb,
    },
];

function loadSettings(): Record<string, boolean> {
    try {
        const raw = localStorage.getItem(SETTINGS_KEY);
        if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
    } catch {
        // ignore
    }
    return { ...DEFAULT_SETTINGS };
}

function saveSettings(settings: Record<string, boolean>) {
    try {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
        // ignore
    }
}

// ---------------------------------------------------------------------------
// Tab: Weekly Check-In
// ---------------------------------------------------------------------------

const WeeklyCheckinTab: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<WeeklyCheckin | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getWeeklyCheckin();
            setData(result);
        } catch {
            setError('Failed to load weekly check-in. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <div className="checkin-loading">
                <div className="checkin-loading-spinner" />
                <span>Preparing your weekly check-in...</span>
            </div>
        );
    }

    if (error) {
        return (
            <GlassCard>
                <div className="checkin-loading">
                    <p style={{ color: 'var(--danger)' }}>{error}</p>
                    <GlassButton variant="secondary" onClick={load} icon={<RefreshCw size={16} />}>
                        Try Again
                    </GlassButton>
                </div>
            </GlassCard>
        );
    }

    if (!data) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            {/* Summary */}
            <GlassCard variant="strong" padding="lg">
                <div className="checkin-summary-card">
                    <p className="checkin-summary-text">{data.summary}</p>

                    {data.highlights && data.highlights.length > 0 && (
                        <div className="checkin-highlights-section">
                            <p className="checkin-section-label">This Week's Highlights</p>
                            <ul className="checkin-highlights-list">
                                {data.highlights.map((h, i) => (
                                    <li key={i} className="checkin-highlight-item">
                                        <span className="checkin-highlight-dot" />
                                        {h}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </GlassCard>

            {/* Recommended actions */}
            {data.recommended_actions && data.recommended_actions.length > 0 && (
                <>
                    <p className="checkin-section-label">Recommended Actions</p>
                    <div className="checkin-actions-grid">
                        {data.recommended_actions.map((action, i) => (
                            <GlassCard key={i} variant="hover" padding="md" className="checkin-action-card">
                                <p className="checkin-action-name">{action.action}</p>
                                <p className="checkin-action-desc">{action.description}</p>
                                <GlassButton
                                    variant="primary"
                                    size="sm"
                                    icon={<ExternalLink size={14} />}
                                    iconPosition="right"
                                    onClick={() => navigate(action.url)}
                                >
                                    Do this
                                </GlassButton>
                            </GlassCard>
                        ))}
                    </div>
                </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <GlassButton
                    variant="secondary"
                    size="sm"
                    onClick={load}
                    icon={<RefreshCw size={14} />}
                >
                    Refresh
                </GlassButton>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Tab: All Notifications
// ---------------------------------------------------------------------------

const AllNotificationsTab: React.FC = () => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterPriority, setFilterPriority] = useState<string>('all');
    const [filterType, setFilterType] = useState<string>('all');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getNotifications({ limit: 100 });
            setNotifications(data);
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleMarkRead = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        await markNotificationRead(id);
        setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
    };

    const handleDismiss = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        await dismissNotification(id);
        setNotifications(prev => prev.filter(n => n.notification_id !== id));
    };

    const handleDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        await deleteNotification(id);
        setNotifications(prev => prev.filter(n => n.notification_id !== id));
    };

    const handleMarkAllRead = async () => {
        await markAllNotificationsRead();
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    };

    const handleItemClick = async (notif: Notification) => {
        if (!notif.read) {
            await markNotificationRead(notif.notification_id);
            setNotifications(prev =>
                prev.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n)
            );
        }
        if (notif.action_url) navigate(notif.action_url);
    };

    // Filter
    const filtered = notifications.filter(n => {
        if (filterPriority !== 'all' && n.priority !== filterPriority) return false;
        if (filterType !== 'all' && n.type !== filterType) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return n.title.toLowerCase().includes(q) || n.body.toLowerCase().includes(q);
        }
        return true;
    });

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Toolbar */}
            <div className="all-notifications-toolbar">
                <div className="all-notifications-filters">
                    <input
                        type="text"
                        placeholder="Search notifications..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="all-notifications-search"
                        style={{
                            padding: '6px 12px',
                            background: 'var(--panel)',
                            border: '1px solid var(--stroke)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--text)',
                            fontSize: 'var(--text-sm)',
                        }}
                    />
                    <select
                        value={filterPriority}
                        onChange={e => setFilterPriority(e.target.value)}
                        style={{
                            padding: '6px 10px',
                            background: 'var(--panel)',
                            border: '1px solid var(--stroke)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--text)',
                            fontSize: 'var(--text-sm)',
                        }}
                    >
                        <option value="all">All priorities</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                    </select>
                    <select
                        value={filterType}
                        onChange={e => setFilterType(e.target.value)}
                        style={{
                            padding: '6px 10px',
                            background: 'var(--panel)',
                            border: '1px solid var(--stroke)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--text)',
                            fontSize: 'var(--text-sm)',
                        }}
                    >
                        <option value="all">All types</option>
                        <option value="cadence_alert">Publishing</option>
                        <option value="campaign_deadline">Campaign</option>
                        <option value="weekly_checkin">Weekly</option>
                        <option value="content_suggestion">Suggestion</option>
                    </select>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                    {unreadCount > 0 && (
                        <GlassButton variant="secondary" size="sm" onClick={handleMarkAllRead} icon={<CheckCheck size={14} />}>
                            Mark all read
                        </GlassButton>
                    )}
                    <GlassButton variant="secondary" size="sm" onClick={load} icon={<RefreshCw size={14} />}>
                        Refresh
                    </GlassButton>
                </div>
            </div>

            {/* List */}
            {loading && notifications.length === 0 ? (
                <div className="checkin-loading">
                    <div className="checkin-loading-spinner" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="all-notifications-empty">
                    <span className="all-notifications-empty-icon"><CheckCheck size={40} /></span>
                    <span style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                        {notifications.length === 0 ? "No notifications yet" : "No notifications match your filters"}
                    </span>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                        {notifications.length === 0
                            ? "AiMe will alert you when something needs your attention"
                            : "Try clearing filters to see all notifications"}
                    </span>
                </div>
            ) : (
                <div className="all-notifications-list">
                    {filtered.map(notif => (
                        <div
                            key={notif.notification_id}
                            className={`all-notification-row${!notif.read ? ' unread' : ''}`}
                            onClick={() => handleItemClick(notif)}
                        >
                            <span className={`all-notification-priority-dot ${notif.priority}`} />
                            <div className="all-notification-content">
                                <p className="all-notification-title">{notif.title}</p>
                                <p className="all-notification-body">{notif.body}</p>
                                <div className="all-notification-meta">
                                    <span className="all-notification-time">{timeAgo(notif.created_at)}</span>
                                    <GlassBadge variant={typeBadgeVariant(notif.type)} size="sm">
                                        {typeLabel(notif.type)}
                                    </GlassBadge>
                                    <GlassBadge
                                        variant={notif.priority === 'high' ? 'danger' : notif.priority === 'medium' ? 'warning' : 'default'}
                                        size="sm"
                                    >
                                        {notif.priority}
                                    </GlassBadge>
                                </div>
                            </div>
                            <div className="all-notification-controls" onClick={e => e.stopPropagation()}>
                                {!notif.read && (
                                    <button
                                        className="all-notification-btn"
                                        onClick={e => handleMarkRead(notif.notification_id, e)}
                                        title="Mark as read"
                                    >
                                        <Check size={14} />
                                    </button>
                                )}
                                <button
                                    className="all-notification-btn"
                                    onClick={e => handleDismiss(notif.notification_id, e)}
                                    title="Dismiss"
                                >
                                    <TrendingUp size={14} style={{ transform: 'rotate(-45deg)' }} />
                                </button>
                                <button
                                    className="all-notification-btn danger"
                                    onClick={e => handleDelete(notif.notification_id, e)}
                                    title="Delete"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Tab: Settings
// ---------------------------------------------------------------------------

const SettingsTab: React.FC = () => {
    const [settings, setSettings] = useState<Record<string, boolean>>(loadSettings);
    const [runningChecks, setRunningChecks] = useState(false);
    const [lastRun, setLastRun] = useState<{ created: number; checks: string[] } | null>(null);

    const handleToggle = (key: string) => {
        const updated = { ...settings, [key]: !settings[key] };
        setSettings(updated);
        saveSettings(updated);
    };

    const handleRunChecks = async () => {
        setRunningChecks(true);
        try {
            const result = await runCoordinatorChecks();
            setLastRun({ created: result.notifications_created, checks: result.checks_run });
        } catch {
            setLastRun({ created: 0, checks: ['error'] });
        } finally {
            setRunningChecks(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <GlassCard variant="strong" padding="md">
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
                    Choose which types of alerts and recommendations AiMe proactively surfaces for you.
                    Preferences are stored locally in your browser.
                </p>
            </GlassCard>

            <div className="coordinator-settings-list">
                {SETTINGS_META.map(({ key, name, desc, icon: Icon }) => (
                    <div key={key} className="coordinator-setting-row">
                        <span style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
                            <Icon size={18} />
                        </span>
                        <div className="coordinator-setting-info">
                            <span className="coordinator-setting-name">{name}</span>
                            <span className="coordinator-setting-desc">{desc}</span>
                        </div>
                        <label className="coordinator-toggle">
                            <input
                                type="checkbox"
                                checked={settings[key] ?? true}
                                onChange={() => handleToggle(key)}
                            />
                            <span className="coordinator-toggle-track" />
                        </label>
                    </div>
                ))}
            </div>

            {/* Manual trigger */}
            <GlassCard padding="md">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', margin: 0 }}>
                        Manual Check
                    </p>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', margin: 0 }}>
                        Trigger all coordinator checks now to generate any pending alerts without waiting for the daily jobs.
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                        <GlassButton
                            variant="secondary"
                            size="sm"
                            loading={runningChecks}
                            onClick={handleRunChecks}
                            icon={<Radar size={14} />}
                        >
                            Run Checks Now
                        </GlassButton>
                        {lastRun && (
                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                                Last run created {lastRun.created} notification(s)
                            </span>
                        )}
                    </div>
                </div>
            </GlassCard>
        </div>
    );
};

// ---------------------------------------------------------------------------
// CoordinatorModule (main component)
// ---------------------------------------------------------------------------

const TABS = [
    { id: 'checkin', label: 'Weekly Check-In' },
    { id: 'notifications', label: 'All Notifications' },
    { id: 'settings', label: 'Settings' },
];

const CoordinatorModule: React.FC = () => {
    const [activeTab, setActiveTab] = useState('checkin');

    return (
        <div className="coordinator-module">
            <ModuleHeader
                title="Proactive Coordinator"
                breadcrumb={['AiMe', 'Coordinator']}
                actions={[
                    <GlassBadge key="status" variant="success" size="sm">Active</GlassBadge>,
                ]}
            />

            <div className="coordinator-tabs-row">
                <GlassTabs
                    tabs={TABS}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                />
            </div>

            {activeTab === 'checkin' && <WeeklyCheckinTab />}
            {activeTab === 'notifications' && <AllNotificationsTab />}
            {activeTab === 'settings' && <SettingsTab />}
        </div>
    );
};

export default CoordinatorModule;
